import {
  CRAPS_BETS,
  crapsBetWorking,
  crapsOffBetsHit,
  describeCrapsRoll,
  type CrapsBetId,
  type CrapsRules,
  type PublicCraps,
  type PublicSeat,
} from '@tv-casino/shared';

/** One seat's chips on one spot. Craps is all public, so everyone's show. */
export interface CrapsStack {
  seatIndex: number;
  /** Chips on the spot right now. */
  amount: number;
  /** True for the phone holding this component: their chips read differently. */
  mine: boolean;
  /**
   * Awake for the next roll. Only a place or hardway bet on a come-out it was
   * not called working through reads false; once a point is on, every stake
   * works, whatever its come-out call says.
   */
  working: boolean;
  name?: string | null;
}

/** What the last roll did to a spot, for the flash that follows it. */
export type CrapsSpotMark = 'win' | 'lose';

/**
 * Turning published craps state into a picture of the felt. Nobody holds a
 * card in craps, so every chip on the layout is public: the phone and the TV
 * draw the same stacks from the same numbers, and only "which one is mine"
 * differs between them.
 */

export type CrapsStackMap = Partial<Record<CrapsBetId, CrapsStack[]>>;

/**
 * Every stake on the layout, grouped by spot and in seat order. `mySeatIndex`
 * marks the one stack the phone may pull; pass null on the TV.
 */
export function crapsStacks(
  craps: PublicCraps | null,
  seats: readonly PublicSeat[],
  mySeatIndex: number | null,
): CrapsStackMap {
  const out: CrapsStackMap = {};
  if (!craps) return out;
  for (const cs of craps.seats) {
    const seat = seats[cs.index] ?? null;
    for (const bet of cs.bets) {
      const stack: CrapsStack = {
        seatIndex: cs.index,
        amount: bet.amount,
        mine: cs.index === mySeatIndex,
        working: crapsBetWorking(bet, craps.point),
        name: seat?.name ?? null,
      };
      (out[bet.id] ??= []).push(stack);
    }
  }
  return out;
}

/**
 * What the last roll did, spot by spot. A bet id resolves the same way for
 * everyone who has chips on it, so one mark per spot says it all.
 */
export function crapsMarks(craps: PublicCraps | null): Partial<Record<CrapsBetId, CrapsSpotMark>> {
  const out: Partial<Record<CrapsBetId, CrapsSpotMark>> = {};
  for (const s of craps?.lastSettlements ?? []) {
    if (s.resolution === 'win') out[s.betId] = 'win';
    else if (s.resolution === 'lose') out[s.betId] = 'lose';
  }
  return out;
}

/**
 * The stickman's call for the last roll, naming any bet on the felt it landed
 * on while that bet was off — so a 4-4 come-out under a parked hard 8 says why
 * nothing paid. Null before the first roll.
 */
export function crapsRollCall(craps: PublicCraps | null): string | null {
  if (!craps?.lastRoll || !craps.lastEvent) return null;
  const bets = craps.seats.flatMap((cs) => cs.bets);
  return describeCrapsRoll(
    craps.lastRoll,
    craps.lastEvent,
    crapsOffBetsHit(bets, craps.lastRoll, craps.lastEvent),
  );
}

/** Everything one seat has riding on the felt right now. */
export const crapsSeatStaked = (craps: PublicCraps | null, seatIndex: number | null): number =>
  seatIndex === null
    ? 0
    : (craps?.seats[seatIndex]?.bets ?? []).reduce((sum, bet) => sum + bet.amount, 0);

// --- the printed felt -------------------------------------------------------

/** One printed spot: the bet it takes, the big word on the cloth, its odds. */
export interface CrapsSpot {
  id: CrapsBetId;
  label: string;
  /** The small print under the label, straight off the bet's own odds. */
  odds: string;
}

const spots = (cells: [CrapsBetId, string][]): CrapsSpot[] =>
  cells.map(([id, label]) => ({ id, label, odds: CRAPS_BETS[id].odds }));

/**
 * The felt as it is printed on a real table, in reading order: box numbers
 * along the top (SIX and NINE spelled out, the way a table prints them so they
 * cannot be read upside down), the hardways and one-roll props in the centre
 * box, then the field and the two lines along the rail. Three renderers print
 * this — the web's stylesheet, the TV stage and the phone — so the cells and
 * their words live here rather than in each of them.
 */
export const crapsBoxSpots = (): CrapsSpot[] =>
  spots([
    ['place4', '4'],
    ['place5', '5'],
    ['place6', 'SIX'],
    ['place8', '8'],
    ['place9', 'NINE'],
    ['place10', '10'],
  ]);

export const crapsHardSpots = (): CrapsSpot[] =>
  spots([
    ['hard4', '4'],
    ['hard6', '6'],
    ['hard8', '8'],
    ['hard10', '10'],
  ]);

export const crapsPropSpots = (): CrapsSpot[] =>
  spots([
    ['two', '2'],
    ['three', '3'],
    ['eleven', '11'],
    ['twelve', '12'],
    ['anyCraps', 'ANY CRAPS'],
    ['any7', 'ANY 7'],
  ]);

/** The field's small print: what it pays, and what the house rule does to 12. */
export const crapsFieldOdds = (rules: CrapsRules): string =>
  `2 3 4 9 10 11 12 · 2 pays double, 12 pays ${rules.fieldTriple12 ? 'triple' : 'double'}`;

export const crapsDontLineSpots = (): CrapsSpot[] =>
  spots([
    ['dontPass', "DON'T PASS BAR 12"],
    ['dontOdds', 'LAY'],
  ]);

export const crapsPassLineSpots = (): CrapsSpot[] =>
  spots([
    ['pass', 'PASS LINE'],
    ['passOdds', 'ODDS'],
  ]);

/**
 * True when a spot's chips are asleep: a come-out with place or hardway money
 * still on the cloth. Anyone's chips count, not only the phone's — a TV holds
 * no seat and still has to show a sleeping bet.
 */
export const crapsSpotParked = (
  id: CrapsBetId,
  point: number | null,
  here: readonly CrapsStack[],
): boolean => CRAPS_BETS[id].offOnComeOut && point === null && here.some((s) => !s.working);
